/**
 * Reading and driving a running activity, as one signed-in person.
 *
 * The state is filtered in the cloud to what the caller's space role may see, so a page never
 * decides what to hide.
 */

import type { PressTime } from "@nebulize/overlay/interact";
import { modifier, type ActiveEffect, type AttributeModifier, type CanvasArrangement, type RegionMap, type SceneDocument } from "@nebulize/sdk";

import { request } from "./api";
import { remember, type Held } from "./tabSession";

export interface AttributeValue {
  value: number;
  min: number | null;
  max: number | null;
}

/** One short string an activity wrote: a name, a label, a line a control is drawn with. */
export interface TextValue {
  value: string;
}

/**
 * One entity as this app holds it, shaped so it is also an sdk `Entity`.
 *
 * The optional three are what a scene needs and a hand-drawn page never did: `scope` and `facet`
 * are what a `repeat` matches on, and `text_attributes` is what a string binding substitutes. They
 * are optional because `/state` answers a poll that carries no scene, and pretending otherwise
 * would be a lie in the type rather than a missing field at runtime.
 */
export interface StateEntity {
  id: string;
  type: string;
  activity_id: string;
  attributes: Record<string, AttributeValue>;
  tags: { tags: string[] };
  scope?: string;
  facet?: string | null;
  text_attributes?: Record<string, TextValue>;
  /** RUN-044's modifier set, by attribute name. Absent or empty means the base is the current value. */
  modifiers?: Record<string, AttributeModifier[]>;
  /** RUN-002's running effect set, keyed by source. Absent or empty means nothing is applying. */
  effects?: Record<string, ActiveEffect>;
}

export interface CanvasState {
  mount_id: string;
  activity_id: string;
  role: string;
  audiences: string[];
  entities: StateEntity[];
  /** The canvas kinds this connection resolved to, as the welcome named them. */
  canvases?: string[];
  /** What each activity draws on those kinds. Absent on the poll, present on the stream. */
  scenes?: SceneDocument[];
  /** Where each declared region's cells are, which a scene node naming one needs to draw. */
  regions?: RegionMap[];
  /** Where the customer put each activity on each canvas of this space. `OVL-027`. */
  arrangement?: CanvasArrangement;
}

export interface LiveActivityEntry {
  mount_id: string;
  activity_id: string;
  name: string;
}

export interface SpaceEntry {
  space_id: string;
  name: string;
  join_code: string;
  role: string;
  activities: LiveActivityEntry[];
}

export interface Me {
  account_id: string;
  display_name: string;
}

/** Who this browser is signed in as. The participant id on a submission is this account's id. */
export function whoAmI(): Promise<Me> {
  return request<Me>("/v1/accounts/me");
}

/** Sign this tab in as one of the local people, and keep the credential in this tab only. */
export async function signInThisTab(subject: string): Promise<void> {
  const held = await request<Held>("/v1/auth/twitch/tab-session", {
    method: "POST",
    body: JSON.stringify({ subject }),
  });
  remember(held);
}

/** Join the one address a host hands out, for whoever followed it. A no-op for an existing member
 * (the owner included); a first-time visitor becomes one on the spot. */
export function joinSpace(code: string): Promise<SpaceEntry> {
  return request<SpaceEntry>("/v1/spaces/join", {
    method: "POST",
    body: JSON.stringify({ join_code: code }),
  });
}

/** Resolve the account's front door: whatever space that account currently has loaded. */
export function enterHandle(handle: string): Promise<SpaceEntry> {
  return request<SpaceEntry>(`/v1/spaces/by-handle/${encodeURIComponent(handle)}`);
}

/**
 * One filtered read of a run, for a caller with no socket.
 *
 * `asCanvas` asks to look through one canvas the activity declares instead of the reader's own
 * role. The api grants it to the space's owner and ignores it for everybody else, so the
 * `canvases` field on the answer is what was really granted and never an assumption.
 */
export function readState(mountId: string, asCanvas?: string): Promise<CanvasState> {
  const asking = asCanvas === undefined ? "" : `?as_canvas=${encodeURIComponent(asCanvas)}`;
  return request<CanvasState>(`/v1/mounts/${mountId}/state${asking}`);
}

/** One declared submission. `when` names at most one press time; absent is stamped on arrival. */
export function submit(
  mountId: string,
  submission: string,
  value: string,
  when?: PressTime,
): Promise<unknown> {
  return request(`/v1/mounts/${mountId}/submissions`, {
    method: "POST",
    body: JSON.stringify({
      submission,
      arguments: { value },
      ...(when?.occurred_at === undefined ? {} : { occurred_at: when.occurred_at }),
      ...(when?.occurred_after === undefined ? {} : { occurred_after: when.occurred_after }),
    }),
  });
}

export function has(entity: StateEntity, tag: string): boolean {
  return entity.tags.tags.includes(tag);
}

/** The base value: what was written, before any modifier. Read this to write or to submit. */
export function attribute(entity: StateEntity, name: string): number {
  return entity.attributes[name]?.value ?? 0;
}

/**
 * The current value: the base with RUN-044's modifier set applied. Read this to display a
 * number to a person, never to build a write or a submission argument.
 */
export function currentAttribute(entity: StateEntity, name: string): number {
  const reading = entity.attributes[name];
  if (reading === undefined) return 0;
  return modifier.evaluateAttribute(reading, entity.modifiers?.[name] ?? []);
}

interface Placed {
  entity: StateEntity;
  row: number;
  column: number;
}

/** Cells whose ids are `cell_<row>_<column>`, laid out as rows. Size is read off the ids. */
export function asGrid(entities: StateEntity[]): StateEntity[][] {
  const placed = entities
    .map((entity): Placed | null => {
      const found = /^cell_(\d+)_(\d+)$/.exec(entity.id);
      return found === null
        ? null
        : { entity, row: Number(found[1]), column: Number(found[2]) };
    })
    .filter((one): one is Placed => one !== null);

  const rows = Math.max(0, ...placed.map((one) => one.row + 1));
  const columns = Math.max(0, ...placed.map((one) => one.column + 1));
  return Array.from({ length: rows }, (_, row) =>
    Array.from({ length: columns }, (_, column) =>
      placed.find((one) => one.row === row && one.column === column)?.entity,
    ).filter((one): one is StateEntity => one !== undefined),
  );
}
